import * as amplitude from "@amplitude/analytics-browser";
import { useCallback } from "react";
import moment from "moment";

import { usePlan } from "./usePlan";
import { useStable } from "./useStable";
import { useAuth } from "../../lib/auth/AuthContext";
import { getCookieBotConsentStatistics } from "../utils/cookieConsent";
import { useAppState } from "../../lib/appContext/AppContext";
import i18n from "../../i18n";

type TrackingEmitter = (
  additionalData?: Record<string, unknown>
) => Promise<void>;

type TrackingProperties = Record<string, unknown>;

interface ITrackingState extends TrackingProperties {
  module: string;
  email: string | undefined;
  season: number;
  organization: string | undefined;
  farmId: string;
  fieldId: string;
}

interface IOrganizationIdentity {
  id?: string;
  name?: string;
}

const getTrackingStatePayload = (
  trackingState: ITrackingState,
  hasStatisticsConsent: boolean
): TrackingProperties => {
  if (hasStatisticsConsent) {
    return trackingState;
  }

  const sanitizedState = { ...trackingState };
  delete sanitizedState.email;
  return sanitizedState;
};

const getTrackingEventName = (
  defaultName: string,
  stableProperties?: TrackingProperties,
  additionalData?: TrackingProperties
): string => {
  return (
    (typeof additionalData?.event === "string"
      ? additionalData.event
      : undefined) ??
    (typeof stableProperties?.event === "string"
      ? stableProperties.event
      : undefined) ??
    defaultName
  );
};

/**
 * to track events and actions in amplitude, we are using this hook to get the common properties and
 * emit the event to amplitude.
 * @param name The name of the event to be tracked.
 * @param properties The properties associated with the event.
 * @param module The module from which the event is being tracked.
 * @returns A function that can be used to emit tracking events to Amplitude.
 */
export const useAmplitudeLogging = (
  name: string,
  properties?: TrackingProperties | undefined
): TrackingEmitter => {
  const { stateValue } = useAppState();
  const { user } = useAuth();
  const language = i18n.language;

  const currentOrganization = {
    id: stateValue?.orgValue?.id,
    name: stateValue?.orgValue?.name,
    country: stateValue?.orgValue?.country,
    workspace_id: stateValue?.orgValue?.workspace_id ?? "",
  };
  const plan = usePlan(currentOrganization.workspace_id);
  const stableProperties = useStable(properties);
  const trackingState = useStable<ITrackingState>({
    module:
      typeof stableProperties?.module === "string"
        ? stableProperties.module
        : "",
    email: user?.email,
    season: new Date().getFullYear(),
    organization: currentOrganization.id,
    farmId: stateValue?.farmValue?.id ?? "",
    fieldId: "", // fix this
  });
  const organizationPayload = {
    id: currentOrganization.id,
    name: currentOrganization.name,
    country: currentOrganization.country,
  };
  const workspacePayload = {
    id: stateValue?.workspaceValue?.id,
    name: stateValue?.workspaceValue?.name,
  };
  const farmPayload = {
    id: stateValue?.farmValue?.id ?? "",
    name: stateValue?.farmValue?.name,
    farmTotalHa: stateValue?.farmValue?.details?.farmArea ?? 0, // fix this
    fieldTotalHa: 0,
  };
  const trackingUser = {
    id: user?.id,
    type: user?.type,
    country: user?.country ?? user?.country_code,
  };

  const emit = useCallback(
    async (additionalData?: TrackingProperties): Promise<void> => {
      const hasStatisticsConsent = getCookieBotConsentStatistics();
      const statePayload = getTrackingStatePayload(
        trackingState,
        hasStatisticsConsent
      );
      const userPayload = hasStatisticsConsent ? trackingUser : undefined;

      const payload = Object.freeze({
        state: statePayload,
        ...(userPayload ? { user: userPayload } : {}),
        org: organizationPayload,
        workspace: workspacePayload,
        farm: farmPayload,
        time: moment().format("MM/DD/YYYY h:mm a"),
        language,
        ...stableProperties,
        ...additionalData,
        commercial_plan: plan.data?.planName,
      });
      const eventName = getTrackingEventName(
        name,
        stableProperties,
        additionalData
      );

      if (eventName) {
        amplitude.logEvent(eventName, payload);
      }
    },
    [
      farmPayload,
      language,
      name,
      organizationPayload,
      plan.data?.planName,
      stableProperties,
      trackingState,
      trackingUser,
      workspacePayload,
    ]
  );

  return emit;
};

/**
 * Track a generic-level _event_.
 * hook.
 */
export const useTrackingEvent = (
  properties?: Record<string, unknown> | undefined
): TrackingEmitter => {
  // we are already passing the event name in the properties so to follow the useAmplitudeLogging standards we are passing empty strings here.
  return useAmplitudeLogging("", properties);
};

/**
 * Track an application-level _event_. (Something that occurs _not_ in response to a user action).
 * Only use for top-level events; if you are specifically within a particular module, use a more specific tracking
 * hook.
 */
export const useTrackApplicationEvent = (
  name: string,
  properties?: Record<string, unknown> | undefined
): TrackingEmitter => {
  return useAmplitudeLogging(`event: ${name}`, properties);
};

/**
 * Track an application-level _action_. (Something that a user initiates).
 * Only use for top-level actions; if you are specifically within a particular module, use a more specific tracking
 * hook.
 */
export const useTrackApplicationAction = (
  name: string,
  properties?: Record<string, unknown> | undefined
): TrackingEmitter => {
  return useAmplitudeLogging(`action: ${name}`, properties);
};

/**
 * Set user properties when the organization changes.
 * @param planName The name of the commercial plan.
 * @param org The organization identity.
 */
export const setUserPropertyByOrgChange = (
  planName: string | undefined,
  org: IOrganizationIdentity
): void => {
  const updatedIdentity = new amplitude.Identify();
  planName && updatedIdentity.set("commercial_plan", planName);
  org.id && updatedIdentity.set("org_id", org.id);
  org.name && updatedIdentity.set("org_name", org.name);
  updatedIdentity.set("date_time", new Date().toISOString());
  amplitude.identify(updatedIdentity);
};
